import { Box, Button, Checkbox, FormControlLabel, Typography, Modal, IconButton } from '@mui/material'
import axios from 'axios'
import React, { useEffect, useRef, useState } from 'react'
import { api } from '../utils/api'
import { useDispatch, useSelector } from 'react-redux'
import { handleError } from '../utils/error'
import { socket } from '../utils/socket'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import { fetchStores, selectStores } from '../redux/slices/storeSlice'
import DeleteIcon from '@mui/icons-material/Delete'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import ContentCutIcon from '@mui/icons-material/ContentCut'
import { updateFormActions } from '../redux/store'
import { getFromStorage } from '../utils/storage'
import TagModal from './TagModal'
import CheckIcon from '@mui/icons-material/Check'
import { mainGreen } from '../utils/color'
import { orderPanelStyles } from './common/orderPanelStyles'
import ExtendedIconButton from './button-icons/ExtendedIconButton'
import { useNavigate } from 'react-router-dom'
import { enqueueSnackbar } from 'notistack'
import PanelOrderItem from './PanelOrderItem'
import { OrderStatus } from '../models'

export default function CreateOrderPanel({ createOrdersProps }) {
	//------INIT STATE------
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const createOrders = createOrdersProps
	const [storeConfirm, setStoreConfirm] = useState('')
	const [transferItem, setTransferItem] = useState()
	const [currentTagItem, setCurrentTagItem] = useState(null)
	const [searchText] = useState('')
	const [filteredOrders, setFilteredOrders] = useState(createOrders)
	const { currentUser } = useSelector((state) => state.currentUser)
	const token = getFromStorage('current-user')?.accessToken
	const [showTransferModal, setShowTransferModal] = useState(false)
	const storesFromRedux = useSelector(selectStores)
	const [expandedOrders, setExpandedOrders] = useState({})

	//------SCROLL TO BOTTOM------
	const messagesEndRef = useRef(null)
	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'instant' })
	}

	useEffect(() => {
		scrollToBottom()
	}, [])

	useEffect(() => {
		dispatch(fetchStores())
	}, [dispatch])

	const transferOrder = async function (orderId) {
		try {
			const selectedStore = storesFromRedux.find((store) => store.name === storeConfirm)
			await axios({
				url: api.PUT_TRANSFER_ORDER,
				headers: {
					Authorization: `Bearer ${token}`,
				},
				method: 'PUT',
				data: { orderId, toStore: selectedStore._id },
				withCredentials: true,
			})
			enqueueSnackbar('Đã chuyển đơn hàng tới cửa hàng thành công!')
			setShowTransferModal(false)
			setStoreConfirm('')
		} catch (error) {
			enqueueSnackbar(error?.response?.data?.message, {
				variant: 'error',
			})
			handleError(error)
		}
	}

	const handleTagClick = (item) => {
		setCurrentTagItem(item)
	}

	useEffect(() => {
		setFilteredOrders(createOrders?.filter((order) => order.detail.toLowerCase().includes(searchText.toLowerCase())))
	}, [createOrders, searchText])

	//------POST START PRODUCE ORDERS------
	const waitToConfirmSellExport = async function (data) {
		socket.emit('wait-confirm-sell-export', data)
	}

	//-----DELETE ORDER-----
	const deleteCreateOrder = async function (data) {
		socket.emit('delete-create-order', data)
	}

	return (
		<Box sx={orderPanelStyles.container}>
			<Box sx={orderPanelStyles.ordersBox}>
				{filteredOrders?.map((orderData, index) => (
					<PanelOrderItem
						key={orderData._id}
						order={orderData}
						index={index}
						expandedOrders={expandedOrders}
						renderActions={() => (
							<Box sx={orderPanelStyles.actionIcons}>
								{orderData.status === OrderStatus.PENDING_PRODUCE &&
									currentUser?.permission?.includes('confirmSellExport') && (
										<IconButton
											size='small'
											sx={{ color: mainGreen }}
											onClick={() => navigate('/store-manager/stock/confirm')}>
											<CheckIcon fontSize='small' />
										</IconButton>
									)}

								{orderData.status !== OrderStatus.PENDING_PRODUCE &&
									currentUser?.permission?.includes('receiveProduce') && (
										<IconButton
											size='small'
											sx={{ color: '#1976d2' }}
											onClick={async () => {
												waitToConfirmSellExport({
													producerId: currentUser?.id,
													orderId: orderData._id,
													store: currentUser?.store,
													accessToken: token,
												})
											}}>
											<ArrowForwardIcon sx={{ color: '#2196F3', cursor: 'pointer' }} fontSize='small' />
										</IconButton>
									)}

								{orderData.status !== OrderStatus.PENDING_PRODUCE &&
									(currentUser?.permission?.includes('transferStore') ?? false) && (
										<IconButton
											size='small'
											sx={{ color: '#1976d2' }}
											onClick={(e) => {
												e.stopPropagation()
												setTransferItem(orderData)
												setShowTransferModal(true)
											}}>
											<SwapHorizIcon sx={{ color: '#FF9800', cursor: 'pointer' }} fontSize='small' />
										</IconButton>
									)}

								{currentUser?.permission?.includes('updateOrder') && (
									<IconButton
										size='small'
										sx={{ color: '#1976d2' }}
										onClick={(e) => {
											e.stopPropagation()
											dispatch(updateFormActions.editOrder(orderData))
											dispatch(updateFormActions.showUpdateForm())
										}}>
										<ContentCutIcon sx={{ color: '#9C27B0', cursor: 'pointer' }} fontSize='small' />
									</IconButton>
								)}

								{currentUser?.permission?.includes('updateTag') && (
									<IconButton size='small' sx={{ color: '#ff0000' }} onClick={() => handleTagClick(orderData)}>
										<LocalOfferIcon
											sx={{ color: '#4CAF50', transform: 'rotate(90deg)', cursor: 'pointer' }}
											fontSize='small'
										/>
									</IconButton>
								)}

								{currentUser?.permission?.includes('deleteOrder') && (
									<IconButton
										size='small'
										sx={{ color: 'purple' }}
										onClick={(e) => {
											e.stopPropagation()
											const confirmDelete = window.confirm('Bạn có chắc muốn xóa đơn hàng này?')
											if (confirmDelete) {
												deleteCreateOrder({
													deletor: currentUser.id,
													storeId: currentUser.store,
													orderId: orderData._id,
													accessToken: token,
												})
												enqueueSnackbar('Xóa đơn hàng thành công!', {
													variant: 'success',
												})
											}
										}}>
										<DeleteIcon sx={{ color: '#f44336', cursor: 'pointer' }} fontSize='small' />
									</IconButton>
								)}

								{(orderData?.source || orderData?.code) && (
									<ExtendedIconButton
										isExpanded={expandedOrders[orderData._id]}
										onClick={() => setExpandedOrders((prev) => ({ ...prev, [orderData._id]: !prev[orderData._id] }))}
									/>
								)}
							</Box>
						)}
					/>
				))}
			</Box>

			<Box ref={messagesEndRef}></Box>
			{/* Transfer Store Modal */}
			<Modal open={showTransferModal} onClose={() => setShowTransferModal(false)} sx={orderPanelStyles.modal}>
				<Box sx={orderPanelStyles.modalContent}>
					<Typography variant='h6' sx={{ mb: 2 }}>
						Chọn cửa hàng chuyển đơn
					</Typography>

					{storesFromRedux
						.filter((store) => store._id !== currentUser?.store)
						.map((store) => (
							<FormControlLabel
								key={store._id}
								control={
									<Checkbox
										checked={store.name === storeConfirm}
										onChange={() => {
											setStoreConfirm(store.name)
										}}
									/>
								}
								label={store.name}
								sx={{ display: 'block', mb: 1 }}
							/>
						))}

					<Box sx={orderPanelStyles.modalButtonContainer}>
						<Button onClick={() => setShowTransferModal(false)} variant='outlined' color='inherit'>
							Hủy
						</Button>
						<Button
							onClick={() => {
								const confirmTransfer = window.confirm(
									`Chuyển đơn ${transferItem.shiftCode}${transferItem.shiftIndex} tới ${storeConfirm}?`,
								)
								if (confirmTransfer) {
									transferOrder(transferItem._id)
								}
							}}
							variant='contained'
							color='primary'
							disabled={!storeConfirm}>
							Xác nhận
						</Button>
					</Box>
				</Box>
			</Modal>

			{!!currentTagItem ? (
				<TagModal open={!!currentTagItem} onClose={() => setCurrentTagItem(null)} currentItem={currentTagItem} />
			) : null}
		</Box>
	)
}
